import { Link } from 'react-router-dom'
import { toLang, loc } from '../../config/locales'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { trichologyDetails } from '../../config/trichologyDetails'
import MegaPeekCard from './MegaPeekCard'

interface MegaTrichologyProps {
  lang: string
  onItemClick: () => void
}

const SLUGS = ['energy', 'purify', 'balancy', 'serenity']

const BADGE_BY_SLUG: Record<string, 'popular' | 'bestseller' | undefined> = {
  energy: 'popular',
  purify: 'bestseller',
}

// Energy's hero still ships on a pure-white field; the others sit on warm
// ivory. Point Energy at an ivory-floodfilled menu thumbnail so all four
// blend with the panel instead of one reading as a white box.
const THUMB_OVERRIDE: Record<string, string> = {
  energy:   '/images/trichology/energy/menu-thumb-112w.webp',
  purify:   '/images/trichology/purify/hero-muran-112w.webp',
  balancy:  '/images/trichology/balancy/hero-muran-112w.webp',
  serenity: '/images/trichology/serenity/hero-112w.webp',
}

export default function MegaTrichology({ lang, onItemClick }: MegaTrichologyProps) {
  const { t } = useTranslation()
  const locale = toLang(lang)
  const items = SLUGS.map((slug) => trichologyDetails.find((tx) => tx.slug === slug))
    .filter((tx): tx is NonNullable<typeof tx> => Boolean(tx))

  return (
    <div className="grid grid-cols-12 gap-8">
      <div className="col-span-12 md:col-span-8">
        <h3 className="font-body font-semibold text-[10px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
          {t('megaTrichology.heading')}
        </h3>
        {/* Image-thumbnail links — each treatment shows its MURAN hero still so
            the panel reads full rather than as a sparse four-item text list. */}
        <ul className="grid grid-cols-2 gap-x-5 gap-y-4">
          {items.map((tx) => {
            const badge = BADGE_BY_SLUG[tx.slug]
            const thumb =
              THUMB_OVERRIDE[tx.slug] || tx.heroImage || `/images/gift-guide/${tx.slug}.webp`
            return (
              <li key={tx.slug}>
                <Link
                  to={`/${lang}/trichology/${tx.slug}`}
                  onClick={onItemClick}
                  className="group flex items-center gap-3"
                >
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-ivory">
                    <img
                      src={thumb}
                      alt=""
                      width={112}
                      height={112}
                      loading="eager"
                      fetchPriority="low"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="inline-flex items-center gap-2">
                      <span className="font-body text-sm text-mocha/80 group-hover:text-gold transition-colors">
                        {tx.name}
                      </span>
                      {badge && (
                        <span className="text-[8px] tracking-[0.18em] uppercase text-gold border border-gold/40 px-1.5 py-px rounded-sm">
                          {t(`megaTrichology.badge.${badge}`)}
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-mocha/55 mt-0.5 leading-snug">
                      {t(`megaTrichology.menuSubtitles.${tx.slug}`)}
                    </span>
                    <span className="block text-[11px] text-mocha/35 mt-0.5">
                      {loc(tx.hero.duration, locale)} · {loc(tx.hero.price, locale)}
                    </span>
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>
        <div className="mt-6">
          <Link
            to={`/${lang}/trichology`}
            onClick={onItemClick}
            className="inline-flex items-center gap-1 font-body text-[11px] tracking-[0.18em] uppercase text-gold hover:text-mocha transition-colors"
          >
            {t('megaTrichology.viewAll')} <ArrowRight size={11} />
          </Link>
        </div>
      </div>

      {/* Featured column — 2 trichology cards (story + scan CTA). */}
      <div className="col-span-12 md:col-span-4 md:pl-6 md:border-l border-ivory-dark">
        <h3 className="font-body font-semibold text-[10px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
          {t('megaServices.featured')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4">
          <MegaPeekCard
            to={`/${lang}/trichology`}
            src="/images/trichology/hero-poster.webp"
            onItemClick={onItemClick}
          >
            <p className="mt-3 font-display text-sm text-mocha leading-snug transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:text-gold">
              {t('megaTrichology.featuredCopy')}
              <span
                aria-hidden="true"
                className="mt-1.5 block h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100"
              />
            </p>
          </MegaPeekCard>
          <MegaPeekCard
            to={`/${lang}/trichology`}
            src="/images/trichology/why-scalp-poster.webp"
            onItemClick={onItemClick}
          >
            <p className="mt-3 font-display text-sm text-mocha leading-snug transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:text-gold">
              {t('megaTrichology.featuredScan')}
              <span
                aria-hidden="true"
                className="mt-1.5 block h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100"
              />
            </p>
          </MegaPeekCard>
        </div>
      </div>
    </div>
  )
}
